import { motion } from "framer-motion";
import { Check } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const CHAPTERS = [
  {
    num: "01",
    title: "Finance-first, not tech-first.",
    body: "Most SAP partners focus on technical implementation. I focus on measurable business outcomes — faster closes, stronger controls, cleaner revenue recognition — powered by deep SAP expertise.",
  },
  {
    num: "02",
    title: "Learn by configuring, not watching.",
    body: "Every course is built on real project scenarios, hands-on S/4HANA system practice, and end-to-end configuration. Weekly doubt sessions and a community that answers.",
  },
  {
    num: "03",
    title: "Senior-led, always.",
    body: "No junior hand-offs. Whether it's a greenfield implementation or a certification bootcamp, you work directly with a principal-level architect who has delivered across industries and geographies.",
  },
];

const OUTCOMES = [
  "Accelerate financial close & reporting",
  "Strengthen compliance & controls",
  "Optimize revenue recognition (IFRS 15 / ASC 606)",
  "Connect planning, budgeting & forecasting",
  "Real-time cash & treasury visibility",
  "Enable AI-driven finance operations",
];

const Manifesto = () => {
  return (
    <section id="manifesto" className="relative py-28 lg:py-40" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono2 text-xs sm:text-sm uppercase tracking-[0.25em] text-amber-600 mb-14"
        >
          The Manifesto
        </motion.p>

        <div className="space-y-0 border-t border-slate-200">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: EASE, delay: i * 0.08 }}
              className="group grid lg:grid-cols-12 gap-6 py-12 lg:py-16 border-b border-slate-200"
              data-testid={`manifesto-chapter-${c.num}`}
            >
              <div className="lg:col-span-2 font-mono2 text-5xl lg:text-6xl font-bold text-slate-900/[0.07] group-hover:text-amber-600/40 transition-colors duration-500">
                {c.num}
              </div>
              <h3 className="lg:col-span-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
                {c.title}
              </h3>
              <p className="lg:col-span-6 text-sm sm:text-base text-slate-600 leading-relaxed max-w-xl lg:pt-2">
                {c.body}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-20 rounded-3xl border border-slate-200 bg-[#F8FAFC] p-8 lg:p-12"
        >
          <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-8">What I help CFOs achieve</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-5">
            {OUTCOMES.map((o, i) => (
              <motion.div
                key={o}
                initial={{ opacity: 0, x: -14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: EASE, delay: i * 0.06 }}
                className="flex items-start gap-3"
              >
                <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
                  <Check size={11} className="text-amber-600" />
                </span>
                <span className="text-sm text-slate-700">{o}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Manifesto;
